// @flow

import url from 'url';

import _ from 'lodash';

import cuid from 'cuid';

import Bloodhound from 'rgs-bloodhound/index.js';

import { default as server, kladrRoute } from 'server2';

import type { DataResponse } from 'server2';

import type {
	Settlement,
	Street,
	House,
	CountryModel,
 } from 'calc-common';

import ObservableMap from 'observable-map';


class Dataset {
	constructor (options: any) {
		options = _.defaultsDeep({}, options);
		options = _.defaultsDeep(options, this._getDefaults(options));
		options = _.defaultsDeep(options, this._getDefaultDefaults(options));

		this._options = options;
		if (options.limit) {
			this.limit = options.limit;
		}
		this.display = options.display;
		this.source = new Bloodhound(options.source);
	}

	_getDefaults () {
		return undefined;
	}

	_getDefaultDefaults (options: any) {
		const defaults = {
			server: server,
			display: 'Name',
			source: {
				remote: {
					// HACK, FIXME: this is a totally fake URL just to meet bloodhound API requirements
					url: '{}',
					wildcard: '{}',
				},
				identify: 'ID',
				datumTokenizer: Bloodhound.tokenizers.obj.nonwordRus(options.display || 'Name'),
			},
		};
		return defaults;
	}
}

class DatasetFetchComputedQuery extends Dataset {
	_getDefaultDefaults (...args) {
		const defaults = {
			source: {
				remote: {
					// function that returns the query params for given user input
					query: undefined,
					// server method
					method: undefined,

					// HACK, FIXME: this is a totally fake URL just to meet bloodhound API requirements
					url: url.format({
						protocol: 'rgs-bloodhound:',
						slashes: true,
						host: cuid(),
						query: {
							input: '__WILDCARD__',
						},
					}),
					wildcard: '__WILDCARD__',

					transport: (params, onSuccess, onError) => {
						const input = url.parse(params.url, true).query.input;
						const query = this._options.source.remote.query(input);

						if (!query || _.isEmpty(query)) {
							onSuccess([]);
							return;
						}

						this._options.source.remote.method({ query })
							.then(({ responseJSON: { Data } }) => Data)
							.tap(data => this._addToDictionary(data))
							.then(onSuccess, onError);
					},
				},
			},
		};
		return _.defaultsDeep(defaults, super._getDefaultDefaults(...args));
	}

	_addToDictionary () {
		// noop
	}
}

class KladrSettlementsDataset extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			source: {
				remote: {
					query: search => _.pickBy({
						search,
						region: this._options.region,
					}),
					method: (...args) => this._options.server.any.rest.get(
						[
							kladrRoute.prefix,
							this._options.variant,
							kladrRoute.settlements,
						],
						...args,
					),
				},
			},
			limit: 30,
		};
	}

	_addToDictionary (data: any) {
		const { dictionary } = this._options;

		if (!dictionary) {
			return;
		}

		_.each(data, record => {
			dictionary.biset(record.ID, record.Name, true);
		});
		dictionary.notify();
	}
}

export class KladrFullSettlementsDataset extends KladrSettlementsDataset {
	_getDefaultDefaults (...args) {
		return _.defaults(super._getDefaultDefaults(...args), {
			variant: kladrRoute.full,
		});
	}
}

export class KladrRsaSettlementsDataset extends KladrSettlementsDataset {
	_getDefaultDefaults (...args) {
		return _.defaults(super._getDefaultDefaults(...args), {
			variant: kladrRoute.rsa,
		});
	}
}

class KladrStreetsDataset extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			source: {
				remote: {
					query: search => _.pickBy({
						search,
						region: this._options.region,
					}),
					method: (...args) => this._options.server.any.rest.get(
						[
							kladrRoute.prefix,
							this._options.variant,
							kladrRoute.settlements,
							this._options.settlementKladr,
						],
						...args,
					),
				},
			},
		};
	}

	_addToDictionary (data: any) {
		const { dictionary } = this._options;

		if (!dictionary) {
			return;
		}

		_.each(data, record => {
			dictionary.biset(record.ID, record.Name, true);
		});
		dictionary.notify();
	}
}

export class KladrFullStreetsDataset extends KladrStreetsDataset {
	_getDefaultDefaults (...args) {
		return _.defaults(super._getDefaultDefaults(...args), {
			variant: kladrRoute.full,
		});
	}
}

export class KladrRsaStreetsDataset extends KladrStreetsDataset {
	_getDefaultDefaults (...args) {
		return _.defaults(super._getDefaultDefaults(...args), {
			variant: kladrRoute.rsa,
		});
	}
}

// Не используется в API МП
export class VehicleModelsDataset extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			display: 'FullModelName',
			source: {
				remote: {
					query: search => _.pickBy({
						search,
						vehicleTypeID: this._options.vehicleTypeID,
						includeVehicleModelInfo: this._options.includeVehicleModelInfo,
						mode: this._options.mode,
					}, _.negate(_.isNil)),
					method: (...args) => this._options.server.any.api.get(
						['dictionary', 'detailedModels'], ...args),
				},
				identify: 'ModelID',
			},
		};
	}

	_addToDictionary (data: any) {
		const { dictionary } = this._options;

		if (!dictionary) {
			return;
		}

		dictionary.clear(true);

		_.each(data, record => {
			dictionary.addRecord(record, true);
		});
		dictionary.notify();
	}
}

// Не используется в API МП
export class ExtendedRegionsDataSet extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			display: 'Name',
			source: {
				remote: {
					query: (search) => ({ search }),
					method: (params) => this._options.server.any.api.get(
						['ekasko', 'kladr', 'extended', 'regions'],
						{ query: _.pick(params.query, 'search') }
					),
				},
			},
		};
	}
}

export class ExtendedSettlementsDataSet extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			source: {
				remote: {
					method: (params): Promise<DataResponse<Array<Settlement>>> => {
						return this._options.server.any.rest.get(
							[
								kladrRoute.prefix,
								kladrRoute.extended,
								kladrRoute.settlements,
							],
							{
								query: {
									search: _.get(params.query, 'search'),
									regionId: _.get(params.query, 'regionKladr'),
									take: _.get(params.query, 'take') || 100,
								},
							}
						);
					},
				},
			},
		};
	}
}

// Не используется в API МП
export class ExtendedSettlementsRsaDataSet extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			source: {
				remote: {
					method: (params): Promise<DataResponse<Array<Settlement>>> => {
						return this._options.server.any.api.get(
							['eosago', 'kladr', 'rsa', 'settlements'],
							{
								query: {
									search: _.get(params.query, 'search'),
									regionId: _.get(params.query, 'regionKladr'),
									take: _.get(params.query, 'take') || 100,
								},
							}
						);
					},
				},
			},
		};
	}
}

export class ExtendedStreetsDataSet extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			display: 'Name',
			source: {
				remote: {
					method: (params): Promise<DataResponse<Array<Street>>> => {
						return this._options.server.any.rest.get(
							[
								kladrRoute.prefix,
								kladrRoute.extended,
								kladrRoute.streets,
							],
							{
								query: {
									search: _.get(params.query, 'search'),
									settlementKladr: _.get(params.query, 'settlementKladr'),
									take: _.get(params.query, 'take') || 100,
								},
							},
						);
					},
				},
			},
		};
	}
}

export class ExtendedHousesDataSet extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			display: 'Name',
			source: {
				remote: {
					method: (params): Promise<DataResponse<Array<House>>> => {
						return this._options.server.any.rest.get(
							[
								kladrRoute.prefix,
								kladrRoute.extended,
								kladrRoute.houses,
							],
							{
								query: {
									search: _.get(params.query, 'search'),
									...(params.query.hasStreet && params.query.streetKladr ? {
										streetKladr: _.get(params.query, 'streetKladr')
									} : {}),
									...(!params.query.hasStreet && params.query.settlementKladr ? {
										settlementKladr: _.get(params.query, 'settlementKladr')
									} : {}),
								},
							}
						);
					},
				},
			},
		};
	}
}

export class EkisBaseDataSet extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			display: 'label',
			source: {
				remote: {
					query: (codeTerm) => ({ codeTerm }),
					method: (params) => this._options.server.any.api.get(
						['lk', 'GetEkisBases'],
						{ query: _.pick(params.query, 'codeTerm') }
					),
				},
			},
		};
	}

	_addToDictionary (data: any) {
		const { dictionary } = this._options;


		if (!dictionary) {
			return;
		}

		_.each(data, record => {
			dictionary.biset(record.value, record.label, true);
		});

		dictionary.notify();
	}

}

export class MiteLpuDataSet extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			display: 'Name',
			source: {
				remote: {
					query: (search) => ({ search }),
					method: (params) => {
						return this._options.server.any.cmsapi.get([
							'lpu',
							'mite',
							'cities',
						], {
							query: {
								term: _.get(params.query, 'search'),
							},
						});
					},
				},
			},
		};
	}
}

export class CountriesDataset extends DatasetFetchComputedQuery {
	_getDefaults () {
		return {
			source: {
				remote: {
					method: (params): Promise<DataResponse<Array<CountryModel>>> => {
						return this._options.server.any.rest.get(
							[
								'countries',
								'classifier',
							],
							{
								query: {
									search: _.get(params.query, 'search'),
								},
							}
						);
					},
					query: (search) => {
						return { search };
					},
					transform: (data) => {
						return data.Items;
					},
				},
			},
			limit: 100,
		};
	}

	_addToDictionary (data) {
		const { Items } = data;
		const { dictionary } = this._options;
		_.each(Items, item => {
			dictionary.bisetAbsent(
				item.ID,
				item.Name,
				true
			);
		});
		dictionary.notify();
	}
}

export type ValueFilter = string => boolean;

export function filterFromDictionary (dictionary: ObservableMap) {
	return function (val: any) {
		return dictionary.has(val);
	};
}
